import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  BookOpen,
  AlertCircle,
  Users,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { GoGraph } from "react-icons/go";
import { MdOutlineArrowOutward } from "react-icons/md";
import { BsCheckCircle } from "react-icons/bs";
import { BsCardChecklist } from "react-icons/bs";
import { toast } from 'react-toastify';
import useSuperAdminDashboardStore from '@/stores/SuperAdminStore/SuperAdminDashboardStore';
import type { Task, TaskActionType } from '@/services/PagesServices/SuperAdminDashboardService';
import { LoadingSpinner, ErrorDisplay, DateRangePicker } from '@/Components/common';
import './SuperAdminDashboard.css';

interface StatCardProps {
  title: string;
  value: number;
  increase: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, increase, icon, onClick }) => (
  <div
    className={`s-admin-dashboard-page-stat-card${onClick ? ' s-admin-dashboard-page-stat-card--clickable' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
  >
    <div className="s-admin-dashboard-page-stat-header">
      <div>
        <div className="s-admin-dashboard-page-stat-title">{title}</div>
        <div className="s-admin-dashboard-page-stat-value">{value.toLocaleString()}</div>
      </div>
      <div className="s-admin-dashboard-page-stat-icon">{icon}</div>
    </div>
    <div className="s-admin-dashboard-page-stat-increase">+ {increase} Increased from Last week</div>
  </div>
);

// Maps each stat card to the page it should navigate to when clicked.
const STAT_CARD_TARGETS: Record<string, string> = {
  'Total Users': '/superadmin/users/user-list',
  'Active Learners': '/superadmin/users/user-list',
  'Total Courses': '/superadmin/content/course-management',
  'Pending Tasks': '/superadmin/users/learning-assignment',
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    stats,
    courses,
    tasks,
    learningHoursData,
    learningProgress,
    dateRange,
    loadingStats,
    loadingCourses,
    loadingTasks,
    loadingLearningHours,
    loadingProgress,
    errorStats,
    errorCourses,
    errorTasks,
    errorLearningHours,
    errorProgress,
    fetchStats,
    fetchCourses,
    fetchTasks,
    fetchLearningHoursData,
    fetchLearningProgress,
    fetchAllData,
    setDateRange,
    handleTaskAction,
    removeTask,
    taskActionLoading,
  } = useSuperAdminDashboardStore();

  // Tooltip state for chart hover
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    x: number;
    y: number;
    hours: string;
    week: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    hours: '',
    week: '',
  });

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Handle date range change
  const onDateRangeApply = (startDate: Date, endDate: Date) => {
    setDateRange(startDate, endDate);
  };

  // Navigate to Learning Assignment Tool page
  const onViewDetailsClick = () => {
    navigate('/superadmin/users/learning-assignment');
  };

  // My Tasks action CTA. "Approve"/"Reject"/"Remind" run the backend action;
  // anything else (e.g. "Review") opens the Approvals inbox to act on it.
  const ACTION_TYPES = ['approve', 'reject', 'remind'];
  const handleTaskCta = async (task: Task) => {
    const action = (task.action || '').trim().toLowerCase();
    if (ACTION_TYPES.includes(action)) {
      const res = await handleTaskAction(task.id, action as TaskActionType);
      if (res.success) {
        removeTask(task.id);
        toast.success(res.message || `Task ${action}d`);
      } else {
        toast.error(res.message || `Failed to ${action} task`);
      }
    } else {
      navigate('/approvals');
    }
  };

  // Calculate individual data points for hover interactions
  const dataPoints = useMemo(() => {
    if (!learningHoursData || learningHoursData.length === 0) {
      return [];
    }

    const maxHours = Math.max(...learningHoursData.map((d) => d.hours), 1);
    const width = 500;
    const height = 180;
    const padding = 10;

    return learningHoursData.map((data, index) => {
      const x = padding + (index / (learningHoursData.length - 1)) * (width - 2 * padding);
      const y = height - padding - (data.hours / maxHours) * (height - 2 * padding);
      return { x, y, hours: data.hours, week: data.week };
    });
  }, [learningHoursData]);

  // Handle chart point hover
  const handlePointHover = (event: React.MouseEvent<SVGCircleElement>, hours: number, week: string) => {
    const circle = event.currentTarget;
    const cx = parseFloat(circle.getAttribute('cx') || '0');
    const cy = parseFloat(circle.getAttribute('cy') || '0');

    setTooltipData({
      visible: true,
      x: cx,
      y: cy - 50,
      hours: `${hours} Hours`,
      week,
    });
  };

  // Handle chart point leave
  const handlePointLeave = () => {
    setTooltipData({
      visible: false,
      x: 0,
      y: 0,
      hours: '',
      week: '',
    });
  };

  // Check if initial load is happening
  const isInitialLoading =
    loadingStats && loadingCourses && loadingTasks && loadingLearningHours && loadingProgress;

  // Check if there's a critical error (all failed)
  const hasCriticalError =
    errorStats && errorCourses && errorTasks && errorLearningHours && errorProgress;

  const getIconByType = (iconType: string) => {
    switch (iconType) {
      case 'book':
        return <BookOpen size={24} />;
      case 'alert':
        return <AlertCircle size={24} />;
      case 'check':
        return <BsCheckCircle size={24} />;
      case 'users':
        return <Users size={24} />;
      default:
        return <BookOpen size={24} />;
    }
  };

  if (isInitialLoading) {
    return (
      <div className="s-admin-dashboard-page s-admin-dashboard-page--loading">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (hasCriticalError) {
    return (
      <div className="s-admin-dashboard-page">
        <ErrorDisplay
          title="Failed to Load Dashboard Data"
          message="We couldn't load the dashboard. Please check your connection and try again."
          type="error"
          showRetry
          onRetry={fetchAllData}
        />
      </div>
    );
  }

  return (
    <div className="s-admin-dashboard-page">
      <div className="s-admin-dashboard-page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Your Mkraft enterprise overview — platform health, users, and analytics.</p>
        </div>
        <DateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onApply={onDateRangeApply}
        />
      </div>

      {/* Stats Grid */}
      <div className="s-admin-dashboard-page-stats-grid">
        {loadingStats ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="s-admin-dashboard-page-stat-card s-admin-dashboard-page-stat-card--skeleton">
                <LoadingSpinner size="sm" />
              </div>
            ))}
          </>
        ) : errorStats ? (
          <div className="s-admin-dashboard-page-section-error">
            <ErrorDisplay
              title="Failed to load stats"
              message={errorStats}
              type="error"
              showRetry
              onRetry={fetchStats}
            />
          </div>
        ) : (
          stats?.map((stat, index) => {
            const target = STAT_CARD_TARGETS[stat.title];
            return (
              <StatCard
                key={index}
                title={stat.title}
                value={stat.value}
                increase={stat.increase}
                icon={getIconByType(stat.iconType)}
                onClick={target ? () => navigate(target) : undefined}
              />
            );
          })
        )}
      </div>

      <div
        className="s-admin-dashboard-page-analytics-banner s-admin-dashboard-page-analytics-banner--clickable"
        role="button"
        tabIndex={0}
        onClick={() => navigate('/superadmin/analytics/organization-insights')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/superadmin/analytics/organization-insights');
          }
        }}
      >
        <div className="s-admin-dashboard-page-analytics-icon"><GoGraph /></div>
        <div>
          <div className="s-admin-dashboard-page-analytics-title">Organization Insights</div>
          <div className="s-admin-dashboard-page-analytics-subtitle">Compare every organization and drill into activity →</div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="s-admin-dashboard-page-content-grid">
        {/* Learning Hours Card */}
        <div className="s-admin-dashboard-page-learning-hours-card">
          {loadingLearningHours || loadingProgress ? (
            <div className="s-admin-dashboard-page-section-loading">
              <LoadingSpinner size="md" />
            </div>
          ) : errorLearningHours ? (
            <ErrorDisplay
              title="Failed to load learning hours"
              message={errorLearningHours}
              type="error"
              showRetry
              onRetry={fetchLearningHoursData}
            />
          ) : (
            <div className="s-admin-dashboard-page-learning-hours-row">
              {/* Left Section: Chart */}
              <div className="s-admin-dashboard-page-chart-section">
                <div className="s-admin-dashboard-page-card-header">
                  <h3>Learning Hours Trend</h3>
                  <p>Time Employees spent in daily learnings</p>
                </div>
                <div className="s-admin-dashboard-page-chart-wrapper">
                  <div className="s-admin-dashboard-page-chart-y-axis">
                    <span>40</span>
                    <span>30</span>
                    <span>20</span>
                    <span>10</span>
                  </div>
                  <div className="s-admin-dashboard-page-chart-area">
                    <svg viewBox="0 0 500 200" className="s-admin-dashboard-page-line-chart">
                      {/* Grid lines */}
                      {[0, 1, 2, 3].map((i) => (
                        <line
                          key={i}
                          x1="10"
                          y1={10 + i * 45}
                          x2="490"
                          y2={10 + i * 45}
                          stroke="#f0f0f0"
                          strokeWidth="1"
                        />
                      ))}
                      {/* Chart line */}
                      {dataPoints.length > 0 && (
                        <polyline
                          points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
                          fill="none"
                          stroke="#ff8c42"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                      {/* Data points for hover */}
                      {dataPoints.map((point, index) => (
                        <circle
                          key={index}
                          cx={point.x}
                          cy={point.y}
                          r="6"
                          fill="white"
                          stroke="#ff8c42"
                          strokeWidth="3"
                          className="s-admin-dashboard-chart-data-point"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => handlePointHover(e, point.hours, point.week)}
                          onMouseLeave={handlePointLeave}
                        />
                      ))}
                    </svg>
                    {/* Tooltip */}
                    {tooltipData.visible && (
                      <div
                        className="s-admin-dashboard-chart-tooltip"
                        style={{
                          position: 'absolute',
                          left: `${tooltipData.x}px`,
                          top: `${tooltipData.y}px`,
                          transform: 'translate(-50%, -100%)',
                          background: 'white',
                          padding: '12px 18px',
                          borderRadius: '12px',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                          border: '1px solid #f0f0f0',
                          pointerEvents: 'none',
                          zIndex: 50,
                          minWidth: '120px',
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{tooltipData.week}</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#222' }}>{tooltipData.hours}</div>
                      </div>
                    )}
                    <div className="s-admin-dashboard-page-chart-labels">
                      {learningHoursData?.map((item, i) => (
                        <span key={i}>{item.week}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section: Progress */}
              <div className="s-admin-dashboard-page-progress-section">
                <div className="s-admin-dashboard-page-date-button-small">
                  {learningProgress?.currentMonth || 'Loading...'}
                  <Calendar size={14} />
                </div>

                <div className="s-admin-dashboard-page-progress-info">
                  <div
                    className={`s-admin-dashboard-page-progress-icon ${
                      learningProgress?.trend === 'down' ? 's-admin-dashboard-page-progress-icon--down' : ''
                    }`}
                  >
                    {learningProgress?.trend === 'down' ? <TrendingDown /> : <TrendingUp />}
                  </div>
                  <div>
                    <div className="s-admin-dashboard-page-progress-label">Progress</div>
                    <div className="s-admin-dashboard-page-progress-value">
                      {learningProgress?.totalHours || 0} Hours{' '}
                      <span
                        className={`s-admin-dashboard-page-progress-percent ${
                          learningProgress?.trend === 'down'
                            ? 's-admin-dashboard-page-progress-percent--down'
                            : ''
                        }`}
                      >
                        {learningProgress?.progressPercentage || 0}%{' '}
                        {learningProgress?.trend === 'down' ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Top Courses Card */}
        <div className="s-admin-dashboard-page-top-courses-card">
          {loadingCourses ? (
            <div className="s-admin-dashboard-page-section-loading">
              <LoadingSpinner size="md" />
            </div>
          ) : errorCourses ? (
            <ErrorDisplay
              title="Failed to load courses"
              message={errorCourses}
              type="error"
              showRetry
              onRetry={fetchCourses}
            />
          ) : (
            <>
              <h3>Top 5 Courses</h3>
              <p>Total Number of Registered Employees</p>
              <div className="s-admin-dashboard-page-courses-list">
                {courses?.map((course, i) => (
                  <div key={i} className="s-admin-dashboard-page-course-item">
                    <div className="s-admin-dashboard-page-course-stats">
                      <div className="s-admin-dashboard-page-course-name">{course.name}</div>
                      <span className="s-admin-dashboard-page-course-count">
                        {course.enrolled} of {course.total}
                      </span>
                    </div>
                    <div className="s-admin-dashboard-page-course-progress-bar">
                      <div
                        className="s-admin-dashboard-page-course-progress-fill"
                        style={{ width: `${(course.enrolled / course.total) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tasks Card */}
      <div className="s-admin-dashboard-page-tasks-card">
        <div className="s-admin-dashboard-page-tasks-header">
          <div className="s-admin-dashboard-page-tasks-header-left">
            <div className="s-admin-dashboard-page-tasks-icon"><BsCardChecklist /></div>
            <div>
              <h3>My Tasks</h3>
              <p>Overview of My Important Tasks</p>
            </div>
          </div>
          <button className="s-admin-dashboard-page-view-details-btn" onClick={onViewDetailsClick}>
            View in Details <span><MdOutlineArrowOutward/></span>
          </button>
        </div>

        {loadingTasks ? (
          <div className="s-admin-dashboard-page-section-loading">
            <LoadingSpinner size="md" />
          </div>
        ) : errorTasks ? (
          <ErrorDisplay
            title="Failed to load tasks"
            message={errorTasks}
            type="error"
            showRetry
            onRetry={fetchTasks}
          />
        ) : tasks && tasks.length === 0 ? (
          <div className="s-admin-dashboard-page-empty-tasks">
            <BsCheckCircle size={48} />
            <h4>All caught up!</h4>
            <p>You have no pending tasks</p>
          </div>
        ) : (
          <table className="s-admin-dashboard-page-tasks-table">
            <thead>
              <tr>
                <th className="s-admin-dashboard-page-task-dot-column"></th>
                <th>Task Description</th>
                <th>Due Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tasks && tasks.map((task, i) => (
                <tr key={i}>
                  <td className="s-admin-dashboard-page-task-dot-cell">
                    <div
                      className={`dashboard-page-task-dot ${
                        task.status === 'Urgent'
                          ? 'dashboard-page-task-dot--urgent'
                          : 'dashboard-page-task-dot--pending'
                      }`}
                    />
                  </td>
                  <td>
                    <div className="s-admin-dashboard-page-task-description">
                      <div>
                        <div className="s-admin-dashboard-page-task-type">
                          {task.type}{' '}
                          <span
                            className={`dashboard-page-task-status dashboard-page-task-status--${task.status.toLowerCase()}`}
                          >
                            {task.status}
                          </span>
                        </div>
                        <div className="s-admin-dashboard-page-task-detail">{task.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="s-admin-dashboard-page-task-date">{task.dueDate}</td>
                  <td className="s-admin-dashboard-page-task-action">
                    <button
                      className="s-admin-dashboard-page-action-button"
                      onClick={() => handleTaskCta(task)}
                      disabled={!!taskActionLoading?.[task.id]}
                    >
                      {taskActionLoading?.[task.id] ? '…' : task.action}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
